import type { AudioStyle } from '../state/player-settings.js';
import type { TimingTrack, VerseCue } from '@fcbh/audio';
import { isPublisherMode, loadPublisherChapter, publisher } from '../publisher/mode.js';
import { rotateProxyHost } from '../state/paa.js';

/**
 * Browser-side Bible Brain (DBT v4) accessor for the reader.
 *
 * Fileset IDs follow the LLLVVV + CCDD + media-code scheme:
 *   - chars 1-6  : bible id (lang + version), e.g. ENGESV
 *   - char  7    : collection (C=complete, N=NT, O=OT, P=portion, S=single)
 *   - char  8    : drama char ('1' = non-drama, '2' = dramatized; '_' for text)
 *   - chars 9-10 : media code (DA = drama audio, SA = single audio, ET = text)
 *
 * The audioStyle toggle in the reader UI swaps drama char 1 <-> 2 so the
 * user gets the "background music + voices" mix or the clean narration.
 *
 * For OT books the collection char becomes 'O' (e.g. ENGESVO2DA); for NT
 * books it stays 'N'. We hard-code the testament map for the 66 canonical
 * books so we can pick the right fileset without an extra round-trip.
 */

const DEFAULT_BIBLE_ID = 'ENGESV';
const DBT_DEFAULT_API = 'https://4.dbt.io/api';

/**
 * Resolve the API base on EVERY call (not once at module load) so PAA proxy
 * rotation takes effect immediately — `window.__PROXY_HOST__` can change at
 * runtime when `rotateProxyHost()` advances to a new healthy domain.
 */
function apiBase(): string {
  const h = typeof window !== 'undefined' ? (window as any).__PROXY_HOST__ : undefined;
  return h ? `${h}/api` : DBT_DEFAULT_API;
}

/**
 * Route an absolute media URL (e.g. the signed CloudFront audio path) through
 * the active proxy host's `/m?u=` streaming endpoint when proxying is active,
 * so audio reaches restricted-country readers over the neutral domain. When no
 * proxy host is set the URL is returned unchanged.
 */
export function proxyMediaUrl(absoluteUrl: string): string {
  const h = typeof window !== 'undefined' ? (window as any).__PROXY_HOST__ : undefined;
  if (!h || !absoluteUrl) return absoluteUrl;
  return `${h}/m?u=${encodeURIComponent(absoluteUrl)}`;
}

export type Testament = 'OT' | 'NT';

const TESTAMENT: Record<string, Testament> = {
  GEN: 'OT', EXO: 'OT', LEV: 'OT', NUM: 'OT', DEU: 'OT',
  JOS: 'OT', JDG: 'OT', RUT: 'OT', '1SA': 'OT', '2SA': 'OT',
  '1KI': 'OT', '2KI': 'OT', '1CH': 'OT', '2CH': 'OT', EZR: 'OT',
  NEH: 'OT', EST: 'OT', JOB: 'OT', PSA: 'OT', PRO: 'OT',
  ECC: 'OT', SNG: 'OT', ISA: 'OT', JER: 'OT', LAM: 'OT',
  EZK: 'OT', DAN: 'OT', HOS: 'OT', JOL: 'OT', AMO: 'OT',
  OBA: 'OT', JON: 'OT', MIC: 'OT', NAM: 'OT', HAB: 'OT',
  ZEP: 'OT', HAG: 'OT', ZEC: 'OT', MAL: 'OT',
  MAT: 'NT', MRK: 'NT', LUK: 'NT', JHN: 'NT', ACT: 'NT',
  ROM: 'NT', '1CO': 'NT', '2CO': 'NT', GAL: 'NT', EPH: 'NT',
  PHP: 'NT', COL: 'NT', '1TH': 'NT', '2TH': 'NT', '1TI': 'NT',
  '2TI': 'NT', TIT: 'NT', PHM: 'NT', HEB: 'NT', JAS: 'NT',
  '1PE': 'NT', '2PE': 'NT', '1JN': 'NT', '2JN': 'NT', '3JN': 'NT',
  JUD: 'NT', REV: 'NT',
};

export type AudioFileset = { filesetId: string; audioStyle: AudioStyle };

export function resolveAudioFileset(
  bookId: string,
  audioStyle: AudioStyle,
  bibleId: string = DEFAULT_BIBLE_ID,
): AudioFileset {
  const testament = TESTAMENT[bookId.toUpperCase()] ?? 'NT';
  const collection = testament === 'OT' ? 'O' : 'N';
  const drama = audioStyle === 'dramatized' ? '2' : '1';
  const media = audioStyle === 'dramatized' ? 'DA' : 'SA';
  return { filesetId: `${bibleId}${collection}${drama}${media}`, audioStyle };
}

export function resolveTextFileset(
  bookId: string,
  bibleId: string = DEFAULT_BIBLE_ID,
): string {
  const testament = TESTAMENT[bookId.toUpperCase()] ?? 'NT';
  const collection = testament === 'OT' ? 'O' : 'N';
  return `${bibleId}${collection}_ET`;
}

export function resolveUsxFileset(
  bookId: string,
  bibleId: string = DEFAULT_BIBLE_ID,
): string {
  // USX fileset ID = _ET fileset ID + '-usx'
  // e.g. ENGESVN_ET → ENGESVN_ET-usx
  const etId = resolveTextFileset(bookId, bibleId);
  return `${etId}-usx`;
}

function apiKey(): string | undefined {
  const key = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_BIBLE_BRAIN_KEY;
  return key && key.length > 0 ? key : undefined;
}

export function hasApiKey(): boolean {
  return apiKey() !== undefined;
}

type ChapterMediaItem = {
  book_id: string;
  chapter_start: number;
  verse_start?: number;
  verse_end?: number;
  path?: string;
  duration?: number;
};

type VerseTimestamp = {
  verse_start: number | string;
  verse_end?: number | string;
  timestamp: number;
};

type TextVerseItem = {
  book_id: string;
  book_name?: string;
  chapter: number | string;
  verse_start: number | string;
  verse_end?: number | string;
  verse_text: string;
};

export type ChapterVerse = {
  ref: string;
  number: number;
  text: string;
  paraStyle?: string;        // 'p' | 'q1' | 'q2' | 'q3' | 'b' | 'm' | 's1' | etc.
  isFirstInPara?: boolean;   // true = this verse starts a new paragraph
  headingBefore?: string;    // section heading text preceding this verse
};

export type ChapterAudio = {
  audioUrl: string | null;
  durationSec: number;
  timing: TimingTrack | null;
  /** Which fileset actually served the audio — `null` if the request failed. */
  servedBy: string | null;
};

export type ChapterTextResult = {
  verses: ChapterVerse[];
  servedBy: string | null;
};

export type BookMeta = {
  bookId: string;
  bookName: string;
  testament: Testament;
  chapters: number[];
};

/** Books available for the bible. Cached per bibleId after first call. */
const bookListCache = new Map<string, BookMeta[]>();

export async function loadBooks(
  bibleId: string = DEFAULT_BIBLE_ID,
): Promise<BookMeta[]> {
  // Publisher mode — the book list comes from the baked manifest, never BB.
  if (isPublisherMode()) {
    const m = publisher.value!;
    return m.books.map((b: { bookId: string; bookName: string; chapters: number[] }): BookMeta => ({
      bookId: b.bookId,
      bookName: b.bookName,
      testament: (TESTAMENT[b.bookId.toUpperCase()] ?? 'NT') as Testament,
      chapters: b.chapters,
    }));
  }
  const cached = bookListCache.get(bibleId);
  if (cached) return cached;
  const key = apiKey();
  if (!key) return [];
  const data = await fetchJson(`/bibles/${bibleId}/book`, key);
  const items = (data?.data ?? []) as Array<{
    book_id: string;
    name?: string;
    testament?: string;
    chapters?: number[];
  }>;
  const books: BookMeta[] = items.map((it) => ({
    bookId: it.book_id,
    bookName: it.name ?? it.book_id,
    testament: (it.testament === 'OT' ? 'OT' : 'NT') as Testament,
    chapters: Array.isArray(it.chapters) ? it.chapters : [],
  }));
  bookListCache.set(bibleId, books);
  return books;
}

/**
 * Load chapter audio + timing. Tries the requested audioStyle's fileset
 * first; if that 404s (some chapters only exist in one style), falls
 * through to the other style so the user always gets audio if any exists.
 */
export async function loadChapterAudio(
  bookId: string,
  chapter: number,
  audioStyle: AudioStyle,
  bibleId: string = DEFAULT_BIBLE_ID,
): Promise<ChapterAudio> {
  const empty: ChapterAudio = { audioUrl: null, durationSec: 0, timing: null, servedBy: null };
  // Publisher mode — chapter audio comes from the baked /data/*.json.
  if (isPublisherMode()) {
    const ch = await loadPublisherChapter(bookId, chapter);
    if (!ch || !ch.audio) return empty;
    const timing: TimingTrack | null = ch.timing.length > 0
      ? buildTrack(bookId, chapter, ch.timing.map((t: { verse: number; beginSec: number }): VerseTimestamp => ({
          verse_start: t.verse,
          timestamp: t.beginSec,
        })))
      : null;
    return { audioUrl: ch.audio.url, durationSec: ch.audio.durationSec, timing, servedBy: ch.audio.filesetId };
  }
  const key = apiKey();
  if (!key) return empty;

  const primary = resolveAudioFileset(bookId, audioStyle, bibleId);
  const alt = resolveAudioFileset(
    bookId,
    audioStyle === 'dramatized' ? 'plain' : 'dramatized',
    bibleId,
  );

  const first = await fetchChapterAudio(primary.filesetId, bookId, chapter, key);
  if (first.audioUrl) return first;
  if (primary.filesetId === alt.filesetId) return empty;
  return fetchChapterAudio(alt.filesetId, bookId, chapter, key);
}

async function fetchChapterAudio(
  filesetId: string,
  bookId: string,
  chapter: number,
  key: string,
): Promise<ChapterAudio> {
  const [media, timestamps] = await Promise.all([
    fetchJson(`/bibles/filesets/${filesetId}/${bookId}/${chapter}`, key).catch(() => null),
    fetchJson(`/timestamps/${filesetId}/${bookId}/${chapter}`, key).catch(() => null),
  ]);

  const mediaItems = (media?.data ?? []) as ChapterMediaItem[];
  const rawUrl = mediaItems[0]?.path ?? null;
  if (!rawUrl) return { audioUrl: null, durationSec: 0, timing: null, servedBy: null };

  // Route the signed audio URL through the neutral proxy when PAA is active so
  // media reaches restricted-country readers over the same healthy domain as
  // the API. No-op when __PROXY_HOST__ is unset.
  const audioUrl = proxyMediaUrl(rawUrl);

  const durationSec = mediaItems[0]?.duration ?? 0;
  const timing = timestamps
    ? buildTrack(bookId, chapter, (timestamps.data ?? []) as VerseTimestamp[])
    : null;
  return { audioUrl, durationSec, timing, servedBy: filesetId };
}

export async function loadChapterText(
  bookId: string,
  chapter: number,
  bibleId: string = DEFAULT_BIBLE_ID,
): Promise<ChapterTextResult> {
  // Publisher mode — verses come from the baked /data/*.json. Audio-only
  // bibles (no text fileset) ship verses: [] in publisher.json's chapter
  // records, so this returns empty + the Verse list renders nothing.
  if (isPublisherMode()) {
    const ch = await loadPublisherChapter(bookId, chapter);
    if (!ch) return { verses: [], servedBy: null };
    const verses: ChapterVerse[] = ch.verses.map((v: { number: number; text: string }): ChapterVerse => ({
      ref: `${capitalize(bookId)}.${chapter}.${v.number}`,
      number: v.number,
      text: v.text,
    }));
    return { verses, servedBy: publisher.value?.bibleId ?? null };
  }
  const key = apiKey();
  if (!key) return { verses: [], servedBy: null };
  const filesetId = resolveTextFileset(bookId, bibleId);
  const data = await fetchJson(`/bibles/filesets/${filesetId}/${bookId}/${chapter}`, key).catch(
    () => null,
  );
  const items = (data?.data ?? []) as TextVerseItem[];
  const verses: ChapterVerse[] = items
    .filter((v) => typeof v.verse_text === 'string' && v.verse_text.trim().length > 0)
    .map((v) => {
      const num = typeof v.verse_start === 'string' ? parseInt(v.verse_start, 10) : v.verse_start;
      return {
        ref: `${capitalize(bookId)}.${chapter}.${num}`,
        number: num,
        text: v.verse_text,
      };
    });
  return { verses, servedBy: verses.length > 0 ? filesetId : null };
}

function buildTrack(book: string, chapter: number, stamps: VerseTimestamp[]): TimingTrack | null {
  if (stamps.length === 0) return null;
  const verses: VerseCue[] = [];
  for (let i = 0; i < stamps.length; i++) {
    const stamp = stamps[i];
    if (!stamp) continue;
    const next = stamps[i + 1];
    const num = typeof stamp.verse_start === 'string'
      ? parseInt(stamp.verse_start, 10)
      : stamp.verse_start;
    if (!Number.isFinite(num) || num <= 0) continue;
    const verseRef = `${capitalize(book)}.${chapter}.${num}`;
    verses.push({
      verseRef,
      beginSec: stamp.timestamp,
      endSec: next ? next.timestamp : stamp.timestamp + 6,
    });
  }
  return verses.length > 0 ? { source: 'biblebrain', verses } : null;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Fetch BB JSON via the active API base (re-read each call so proxy rotation
 * applies). On a failure that looks like a blocked/dead proxy — a thrown
 * network error or a 5xx — rotate to the next healthy proxy domain ONCE and
 * retry. The retry is guarded by a flag so it can never loop: a second failure
 * returns null and the caller's existing `.catch(() => null)` handling takes
 * over. Rotation only happens when a proxy is actually in use (rotateProxyHost
 * returns null otherwise), so the default 4.dbt.io path is unaffected.
 */
async function fetchJson(
  path: string,
  key: string,
  retried = false,
): Promise<{ data?: unknown } | null> {
  const url = `${apiBase()}${path}?v=4&key=${encodeURIComponent(key)}`;
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (res.ok) return res.json() as Promise<{ data?: unknown }>;
    // Non-2xx. A 5xx (or 0/opaque) from a proxy smells like a blocked domain;
    // rotate + retry once. 4xx (e.g. 404 for a missing chapter) is a real
    // answer — don't rotate on it.
    if (!retried && res.status >= 500) {
      const next = rotateProxyHost();
      if (next) return fetchJson(path, key, true);
    }
    return null;
  } catch {
    // Thrown = network error / DNS failure / connection reset — the classic
    // signature of a freshly-blocked domain. Rotate + retry once.
    if (!retried) {
      const next = rotateProxyHost();
      if (next) return fetchJson(path, key, true);
    }
    return null;
  }
}

export type SearchHit = {
  ref: string;
  book: string;
  chapter: number;
  verse: number;
  text: string;
};

/**
 * Full-text search via Bible Brain's `/search` endpoint against the
 * current bible's text fileset. Returns up to `limit` ranked verses
 * with verse text inline.
 *
 * Picks the NT text fileset by default since most readers spend time
 * in the NT first; callers can override the fileset with `opts.scope`
 * = 'ot' to search OT, or 'both' to fall through to both filesets.
 */
export async function searchBible(
  query: string,
  bibleId: string = DEFAULT_BIBLE_ID,
  opts: { limit?: number; scope?: 'nt' | 'ot' | 'both' } = {},
): Promise<SearchHit[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];
  const key = apiKey();
  if (!key) return [];
  const limit = Math.min(opts.limit ?? 30, 50);
  const scope = opts.scope ?? 'both';
  const filesets = [
    scope === 'ot' ? null : `${bibleId}N_ET`,
    scope === 'nt' ? null : `${bibleId}O_ET`,
  ].filter((f): f is string => Boolean(f));
  const results: SearchHit[] = [];
  for (const fileset of filesets) {
    const path = `/search?fileset_id=${encodeURIComponent(fileset)}&query=${encodeURIComponent(trimmed)}&limit=${limit}`;
    const data = await fetchJson(path, key).catch(() => null);
    const rows = (data?.data ?? []) as Array<{
      book_id?: string;
      chapter?: number | string;
      verse_start?: number | string;
      verse_text?: string;
    }>;
    for (const row of rows) {
      if (!row.book_id || !row.verse_text) continue;
      const chapter = typeof row.chapter === 'string' ? parseInt(row.chapter, 10) : row.chapter ?? 0;
      const verse = typeof row.verse_start === 'string' ? parseInt(row.verse_start, 10) : row.verse_start ?? 0;
      if (!Number.isFinite(chapter) || !Number.isFinite(verse) || verse <= 0) continue;
      const cap = capitalize(row.book_id);
      results.push({
        ref: `${cap}.${chapter}.${verse}`,
        book: row.book_id,
        chapter,
        verse,
        text: row.verse_text,
      });
      if (results.length >= limit) return results;
    }
  }
  return results;
}
